/**
 * NOLAS 2026 — © Mikael (Mikaël FOURNY). Fork de la plateforme TNE 2026.
 * src/context/EventIdentityProvider.tsx
 *
 * Rôle :
 *   LOT 3 (socle d'identité) — Contexte React qui expose l'identité de
 *   l'événement actif DÉJÀ FORMATÉE (nom, date, lieu, organisateur), prête à
 *   être injectée comme `values` d'un `<FormattedMessage>` / `formatMessage()`.
 *
 *   C'est le point de branchement des clés i18n « identité » : plutôt que
 *   d'appeler `useEventConfig()` + les helpers dans ~40 composants, on monte CE
 *   provider haut dans l'arbre (App.tsx) et chaque écran fait `useEventIdentity()`
 *   puis `values={identity}`. Le token `{eventName}` du catalogue est alors
 *   résolu par react-intl — mécanisme d'interpolation NATIF du projet, aucun
 *   second mécanisme introduit.
 *
 *   Il ENVELOPPE `useEventConfig()`, il ne le remplace pas : les consommateurs
 *   directs de `useEventConfig()` continuent de fonctionner sans modification.
 *
 * Zéro régression :
 *   La valeur par défaut du contexte est dérivée de {@link DEFAULT_EVENT_CONFIG}
 *   (identité NOLAS réelle). Un composant qui appelle `useEventIdentity()` SANS
 *   provider (ex. test unitaire isolé) obtient donc exactement les chaînes
 *   d'origine — le rendu ne change pas, seul le catalogue passe en interpolation.
 *
 * Réutilisable : 100 % — c'est la brique « identité d'événement » de toute app
 *   multi-salon. Les tokens exposés couvrent les 4 formes du problème d'identité
 *   (nom / date / lieu / organisateur), plus les variantes longues nécessaires
 *   aux badges et au pied de page.
 *
 * Voir : hooks/useEventConfig.ts, utils/formatEventDate.ts, utils/formatEventPlace.ts,
 *        app/docs/sprints/LOT-3-socle-identite.md (+ LOT 4 pour la suite).
 *
 * @author Mikael
 * @since  2026-07-23 (LOT 3 — socle d'identité)
 */

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import {
  useEventConfig,
  DEFAULT_EVENT_CONFIG,
  type EventConfig,
} from '@/hooks/useEventConfig';
import { formatEventDateFr } from '@/utils/formatEventDate';
import { formatEventPlace, formatEventPlaceStreet } from '@/utils/formatEventPlace';
import { formatEventTypeForms } from '@/utils/formatEventType';

/**
 * Identité de l'événement, entièrement formatée pour l'affichage. TOUS les
 * champs sont des chaînes NON nulles (chaîne vide si l'info manque) : on peut
 * donc passer l'objet entier en `values={identity}` sans jamais déclencher
 * l'erreur react-intl « missing value » quel que soit le token du message.
 */
export interface EventIdentity {
  // Signature d'index `string` : rend l'objet directement assignable au
  // `Record<string, string>` attendu par le paramètre `values` de react-intl
  // (`<FormattedMessage values={identity} />` / `intl.formatMessage(desc, identity)`).
  // Tous les champs étant des chaînes, la sécurité de type reste raisonnable ;
  // c'est le motif standard d'un objet de `values` i18n.
  readonly [token: string]: string;
  /** Nom du salon — « NOLAS 2026 ». */
  readonly eventName: string;
  /** Date, forme moyenne — « 9 octobre 2026 » (ou « du 9 au 11 octobre 2026 »). */
  readonly eventDate: string;
  /** Date, forme longue — « vendredi 9 octobre 2026 ». */
  readonly eventDateLong: string;
  /** Lieu, nom seul — « SPOT de Thionville ». */
  readonly eventPlace: string;
  /** Rue du lieu — « Place de la Liberté » (pour composer son propre séparateur). */
  readonly eventPlaceAddress: string;
  /** Lieu, forme longue — « SPOT de Thionville — Place de la Liberté ». */
  readonly eventPlaceLong: string;
  /** Organisateur — libellé de l'entité organisatrice. */
  readonly organizer: string;
  // ── Type d'événement (LOT 7) — formes fléchies FR du nom commun du type. ──
  // Remplacent le mot « salon » codé en dur : chaque forme inclut préposition
  // et article pour respecter les accords (« au salon » / « aux portes
  // ouvertes » / « à la biennale »). Voir utils/formatEventType.ts.
  /** Nom nu, minuscule — « salon », « portes ouvertes ». */
  readonly eventType: string;
  /** Nom nu, Capitalisé (début de phrase/label) — « Salon », « Biennale ». */
  readonly eventTypeCap: string;
  /** Article défini + nom — « le salon », « les portes ouvertes ». */
  readonly eventTypeDef: string;
  /** Idem, Capitalisé — « Le salon », « Les portes ouvertes ». */
  readonly eventTypeDefCap: string;
  /** « à » + article — « au salon », « aux portes ouvertes », « à la biennale ». */
  readonly eventTypeAt: string;
  /** « de » + article — « du salon », « des portes ouvertes », « de la biennale ». */
  readonly eventTypeOf: string;
}

/**
 * Dérive l'identité formatée depuis la config brute de l'événement. Fonction
 * PURE (testable sans React) : c'est le cœur réutilisable du provider.
 *
 * @param config Config de l'événement actif (jamais null, cf. useEventConfig).
 * @returns L'identité formatée, tous champs garantis non nuls.
 */
export function computeEventIdentity(config: EventConfig): EventIdentity {
  // Formes fléchies du type (repli « salon » si `type` absent/inconnu).
  const t = formatEventTypeForms(config.type);
  return {
    eventName: config.name,
    eventDate: formatEventDateFr(config, 'medium') ?? '',
    eventDateLong: formatEventDateFr(config, 'long') ?? '',
    eventPlace: formatEventPlace(config, 'short') ?? '',
    eventPlaceAddress: formatEventPlaceStreet(config) ?? '',
    eventPlaceLong: formatEventPlace(config, 'long') ?? '',
    organizer: config.organizer ?? '',
    eventType: t.bare,
    eventTypeCap: t.cap,
    eventTypeDef: t.def,
    eventTypeDefCap: t.defCap,
    eventTypeAt: t.at,
    eventTypeOf: t.of,
  };
}

/**
 * Contexte — sa valeur par défaut est l'identité NOLAS dérivée du placeholder
 * (zéro régression hors provider, cf. en-tête « Zéro régression »).
 */
const EventIdentityContext = createContext<EventIdentity>(
  computeEventIdentity(DEFAULT_EVENT_CONFIG),
);

/**
 * Monte le contexte d'identité. À placer HAUT dans l'arbre (App.tsx), sous le
 * `QueryClientProvider` (il consomme `useEventConfig`). Ne rend aucun markup :
 * il n'enveloppe que ses enfants.
 */
export function EventIdentityProvider({ children }: { children: ReactNode }): JSX.Element {
  const { event } = useEventConfig();
  // Mémoïsé sur la config : on ne recalcule l'identité que quand l'événement
  // change réellement (évite de reformater à chaque rendu de l'arbre).
  const identity = useMemo(() => computeEventIdentity(event), [event]);
  return (
    <EventIdentityContext.Provider value={identity}>
      {children}
    </EventIdentityContext.Provider>
  );
}

/**
 * Renvoie l'identité formatée de l'événement actif. Usage type :
 *
 *   const identity = useEventIdentity();
 *   <FormattedMessage id="app.name" values={identity} />
 *
 * Passer l'objet entier en `values` est le motif recommandé : react-intl ignore
 * les tokens non utilisés, donc `values={identity}` fonctionne pour n'importe
 * quelle clé d'identité — c'est ce qui rend la migration LOT 4 mécanique.
 */
export function useEventIdentity(): EventIdentity {
  return useContext(EventIdentityContext);
}
